import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History, Search } from 'lucide-react';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { useAdminAuditEvents } from 'hooks/useAdminAuditEvents';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { AUDIT_ACTIONS, type AdminAuditEvent, type AdminAuditEventsFilters, type AuditAction } from 'api/adminAuditApi';
import { actionMeta } from 'utils/auditLabels';
import ReferentialPageHeader from 'pages/AdminReferentials/components/ReferentialPageHeader';
import ReferentialBoundary from 'pages/AdminReferentials/components/ReferentialBoundary';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { AUDIT_MODULES, moduleLabel } from './tableColumns';
import PillSelect from './PillSelect';
import AccessDenied from './AccessDenied';
import AuditTable from './AuditTable';
import EventDrawer from './EventDrawer';

const RANGE_DAYS: Record<string, number> = { '7': 7, '30': 30, '90': 90 };
const DEFAULT_RANGE = '30';
const AUDIT_ACTION_SET: readonly string[] = AUDIT_ACTIONS;

/** `?actorId=`/`?action=` from a hand-typed or stale URL: an invalid value is ignored, not sent. */
const validActorId = (raw: string | null): string => (raw && /^\d+$/.test(raw) ? raw : '');
const validAction = (raw: string | null): AuditAction | '' => (raw && AUDIT_ACTION_SET.includes(raw) ? (raw as AuditAction) : '');

/** One filter's `from` cut-off, computed at read time — never a stored date, so "30 derniers
 * jours" stays "30 derniers jours" on a refresh tomorrow instead of freezing to today's value. */
const rangeFrom = (range: string): string | undefined => {
  const days = RANGE_DAYS[range];
  if (!days) return undefined;
  const from = new Date();
  from.setDate(from.getDate() - days);
  return from.toISOString();
};

/**
 * Global action journal (J13, design §6). Gated inline rather than by `ADMIN_ROUTE_GUARDS`, which
 * only redirects silently — this route needs the dedicated "Accès refusé" screen instead (CA10).
 * Filters live in the URL (CA20): a shared link, a refresh or a back-navigation restore the view.
 */
const AdminAudit = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<{ event: AdminAuditEvent; trigger: HTMLElement } | null>(null);

  const hasAuditPermission = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_AUDIT_VIEW);

  const importId = searchParams.get('importId') || undefined;
  // Arriving via "Voir tout l'import" must never hide an older import behind the usual 30-day
  // default — the whole point of that link is to show every row of that one import.
  const defaultRange = importId ? 'all' : DEFAULT_RANGE;
  const range = searchParams.get('range') || defaultRange;
  const actorId = validActorId(searchParams.get('actorId'));
  const source = searchParams.get('source') === 'system' ? 'system' : '';
  const moduleFilter = searchParams.get('module') || '';
  const action = validAction(searchParams.get('action'));
  const q = searchParams.get('q') || '';

  const [searchInput, setSearchInput] = useState(q);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // A navigation that changes `q` without going through the debounce below (the sidebar entry,
  // a shared link, browser back) must still update the box — otherwise it shows stale text.
  useEffect(() => {
    setSearchInput(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Drives the filter bar's own "Réinitialiser": true the moment any pill differs from its
  // default, including picking "Tout l'historique" itself (range !== defaultRange).
  const hasActiveFilters = Boolean(importId || actorId || source || moduleFilter || action || q || range !== defaultRange);
  // Drives the table's empty-state branch instead: "Tout l'historique" is the LEAST restrictive
  // choice, so picking it must not itself count as narrowing the result set (fix round 1, CA12) —
  // unlike `hasActiveFilters`, only an actual period restriction (not the default-vs-chosen diff) counts.
  const periodRestricted = range !== 'all';
  const narrowsResults = Boolean(importId || actorId || source || moduleFilter || action || q || periodRestricted);

  // `rangeFrom` reads `Date.now()`: memoized on `range` alone so it does not mint a new
  // millisecond-different ISO string — and a new react-query key — on every unrelated render.
  const filters: AdminAuditEventsFilters = useMemo(() => {
    const from = rangeFrom(range);
    return {
      ...(importId ? { importId } : {}),
      // `source` wins over `actorId` (CA55) — never both on the same request.
      ...(source ? { source: 'system' as const } : actorId ? { actorId: Number(actorId) } : {}),
      ...(moduleFilter ? { module: moduleFilter } : {}),
      ...(action ? { action } : {}),
      ...(q ? { q } : {}),
      ...(from ? { from } : {}),
    };
  }, [importId, actorId, source, moduleFilter, action, q, range]);

  const audit = useAdminAuditEvents(filters, hasAuditPermission);

  // Minimal, on purpose: the actor options are the distinct actors already on screen, not a
  // second users lookup. "Système" is a static option (below); a deleted user has no stable id
  // to filter by, so it isn't offered.
  const actorOptions = new Map<number, string>();
  audit.events.forEach((event) => {
    if (event.actor) actorOptions.set(event.actor.id, event.actor.name);
  });

  const setFilter = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  };

  // "Système" and a real actor are mutually exclusive (CA55): picking one drops the other.
  const setActorFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'system') {
          next.set('source', 'system');
          next.delete('actorId');
        } else {
          next.delete('source');
          if (value) next.set('actorId', value);
          else next.delete('actorId');
        }
        return next;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchInput('');
    setSearchParams({}, { replace: true });
  };

  // After the Radix trap lets go (rAF), like the record's "Historique" button (CA16).
  const closeDrawer = () => {
    const trigger = selected?.trigger;
    setSelected(null);
    requestAnimationFrame(() => trigger?.focus());
  };

  if (!hasAuditPermission) {
    return <AccessDenied />;
  }

  return (
    <div className="flex flex-col gap-5 p-6">
      <ReferentialPageHeader
        icon={History}
        title={t('Action journal')}
        subtitle={t('All ops actions on the back office: technicians, quotas, and modules to come.')}
      />

      <ReferentialBoundary message={t('Unable to load the journal')}>
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center gap-2.5 border-b px-4 py-3">
              <div className="relative min-w-[220px] flex-1">
                <Search className="pointer-events-none absolute left-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  className="h-[38px] rounded-[9px] pl-[35px]"
                  aria-label={t('Search an entity')}
                  placeholder={t('Search an entity (name, ID)…')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>

              <PillSelect label={t('Period')} value={range} onChange={(v) => setFilter('range', v)}>
                <option value="7">{t('Last 7 days')}</option>
                <option value="30">{t('Last 30 days')}</option>
                <option value="90">{t('Last 90 days')}</option>
                <option value="all">{t('Entire history')}</option>
              </PillSelect>

              <PillSelect label={t('Actor')} value={source === 'system' ? 'system' : actorId} onChange={setActorFilter}>
                <option value="">{t('All actors')}</option>
                <option value="system">{t('System')}</option>
                {Array.from(actorOptions.entries()).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </PillSelect>

              <PillSelect label={t('Module')} value={moduleFilter} onChange={(v) => setFilter('module', v)}>
                <option value="">{t('All modules')}</option>
                {AUDIT_MODULES.map((mod) => (
                  <option key={mod} value={mod}>
                    {moduleLabel(t, mod)}
                  </option>
                ))}
              </PillSelect>

              <PillSelect label={t('Action')} value={action} onChange={(v) => setFilter('action', v)}>
                <option value="">{t('All actions')}</option>
                {AUDIT_ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {actionMeta(a).label(t)}
                  </option>
                ))}
              </PillSelect>

              {hasActiveFilters && (
                <button type="button" onClick={resetFilters} className="text-[13px] font-medium text-muted-foreground underline hover:text-foreground">
                  {t('Reset')}
                </button>
              )}

              {/* CA18 spirit: no "0 actions" flash before the first page lands. */}
              {!audit.isLoading && (
                <span className="ml-auto text-[12.5px] text-muted-foreground">
                  {t('{{count}} action', { count: audit.total })}
                </span>
              )}
            </div>

            <AuditTable
              events={audit.events}
              total={audit.total}
              since={audit.since}
              retentionMonths={audit.retentionMonths}
              isLoading={audit.isLoading}
              isError={audit.isError}
              narrowsResults={narrowsResults}
              onRetry={() => audit.refetch()}
              onResetFilters={resetFilters}
              hasNextPage={!!audit.hasNextPage}
              isFetchingNextPage={audit.isFetchingNextPage}
              isFetchNextPageError={audit.isFetchNextPageError}
              onLoadMore={() => audit.fetchNextPage()}
              onOpenEvent={(event, trigger) => setSelected({ event, trigger })}
            />
          </CardContent>
        </Card>

        <EventDrawer event={selected?.event ?? null} open={!!selected} onOpenChange={(open) => !open && closeDrawer()} />
      </ReferentialBoundary>
    </div>
  );
};

export default AdminAudit;
